import React, { useEffect, useState, useMemo, useId } from 'react';
import { BiCaretUp, BiCaretDown } from 'react-icons/bi';
import { VscTriangleDown, VscTriangleUp  } from "react-icons/vsc";
import {
    MdChevronLeft,
    MdChevronRight,
    MdOutlineCheckBoxOutlineBlank,
    MdOutlineCheckBox
} from 'react-icons/md';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';

import './index.css';

const ROWS_PER_PAGE = [10, 20, 50];

type TableProps = {
    fixHeader?: boolean;
    className?: string;
    header: any[];
    columnTypes?: string[];
    sortValues?: any[][];
    rows: any[][];
    onSelectCell?: (() => void)[][];
    onSelectRow?: (() => void)[];
    rowsSelected?: boolean[];
    // Per-row opt-out of selection. A row set false renders an empty cell
    // instead of a checkbox, for rows there is nothing to act on -- e.g. a
    // test with no report to print. Omit the prop to keep every row selectable.
    rowsSelectable?: boolean[];
    selectType?: 'row' | 'checkBox';
    /** Called with the SOURCE indices (positions in `rows`) of the selectable
     *  rows on the page currently displayed. Table owns pagination, so only it
     *  can say what those are. */
    onSelectAll?: (pageSourceIndices: number[]) => void;
    navigator?: boolean;
    defaultRowsPerPage?: number;
    tableRef?: React.MutableRefObject<HTMLTableElement>;
};

function Table({
    fixHeader,
    className,
    header,
    columnTypes,
    sortValues,
    rows,
    onSelectCell,
    onSelectRow,
    rowsSelected,
    rowsSelectable,
    selectType = 'row',
    onSelectAll,
    navigator = true,
    defaultRowsPerPage,
    tableRef
}: TableProps) {
    const [rowsPerPage, setRowsPerPage] = useState(navigator ? (defaultRowsPerPage || 20) : rows.length);
    const [pageIndex, setPageIndex] = useState(0);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    // const [combinedData, setCombinedData] = useState([]);
    const [sortedRows, setSortedRows] = useState(rows);
    const [sortedOnSelectCell, setSortedOnselectCell] = useState(onSelectCell);
    // null while unsorted -- display order is then the props' own order.
    const [sortedIndices, setSortedIndices] = useState(null);
    const [sortStandard, setSortStandard] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);  //null, asc, desc
    const rowsPerPageRef = useOutsideClickHandler(() => setDropdownOpen(false));

    // Source indices of the selectable rows on the page being displayed. Source
    // rather than display positions, so they remain correct while sorted; the
    // permutation is `sortedIndices` when a sort is active.
    const pageSourceIndices = (() => {
        const from = rowsPerPage * pageIndex;
        const to = rowsPerPage * (pageIndex + 1);
        const indices = [];
        for (let offset = from; offset < Math.min(to, rows.length); offset++) {
            const sourceIndex = sortedIndices ? sortedIndices[offset] : offset;
            if (sourceIndex === undefined) continue;
            if (rowsSelectable && rowsSelectable[sourceIndex] === false) continue;
            indices.push(sourceIndex);
        }
        return indices;
    })();

    // The header checkbox reflects the page, not the whole set.
    const pageAllSelected = pageSourceIndices.length > 0
        && pageSourceIndices.every(index => rowsSelected && rowsSelected[index]);

    // Each item remembers where it started, so sorting can publish a permutation
    // (sortedIndices below) instead of reordered copies. rowsSelected/onSelectRow
    // are then read live from props through that permutation: without it they stay
    // in the props' original order while sorted rows render and every selection
    // lands on the wrong item, and with sorted *copies* in state a checkbox click
    // would only show up a render late.
    const combinedData = useMemo(() => {
        return rows.map((row, index) => ({
            index,
            row,
            sortValues: sortValues ? sortValues[index] : null,
            onSelectCell: onSelectCell ? onSelectCell[index] : null
        }));
    }, [rows, sortValues, onSelectCell]);

    useEffect(() => {
        if (sortStandard !== null && sortOrder !== null) {
            if (sortValues) {
                let sorted;
                const n = header.indexOf(sortStandard);
                const type = columnTypes ? columnTypes[n] : 'string';
                switch (type) {
                    case 'number':
                        sorted = [...combinedData].sort((a, b) => {
                            const aValue = a.sortValues[n] !== null ? a.sortValues[n] : Number.NEGATIVE_INFINITY;
                            const bValue = b.sortValues[n] !== null ? b.sortValues[n] : Number.NEGATIVE_INFINITY;
                            return sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
                        });
                        break;
                    case 'date':
                        sorted = [...combinedData].sort((a, b) => {
                            const aValue = new Date(a.sortValues[n]).getTime();
                            const bValue = new Date(b.sortValues[n]).getTime();
                            return sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
                        });
                        break;
                    case 'string':
                        sorted = [...combinedData].sort((a, b) => {
                            const aValue = a.sortValues[n] ? a.sortValues[n].toString().toLowerCase() : '';
                            const bValue = b.sortValues[n] ? b.sortValues[n].toString().toLowerCase() : '';
                            return sortOrder === 'desc' ? bValue.localeCompare(aValue) : aValue.localeCompare(bValue);
                        });
                        break;
                    default:
                        break;
                }
                setSortedRows(sorted.map(item => item.row));
                setSortedOnselectCell(sorted.map(item => item.onSelectCell));
                setSortedIndices(sorted.map(item => item.index));
            }
            else {
                let sorted;
                const n = header.indexOf(sortStandard);
                const type = columnTypes ? columnTypes[n] : 'string';
                switch (type) {
                    case 'number':
                        sorted = [...combinedData].sort((a, b) => {
                            const aValue = a.row[n] !== null ? a.row[n] : Number.NEGATIVE_INFINITY;
                            const bValue = b.row[n] !== null ? b.row[n] : Number.NEGATIVE_INFINITY;
                            return sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
                        });
                        break;
                    case 'date':
                        sorted = [...combinedData].sort((a, b) => {
                            const aValue = new Date(a.row[n]).getTime();
                            const bValue = new Date(b.row[n]).getTime();
                            return sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
                        });
                        break;
                    case 'string':
                        sorted = [...combinedData].sort((a, b) => {
                            const aValue = a.row[n] ? a.row[n].toString().toLowerCase() : '';
                            const bValue = b.row[n] ? b.row[n].toString().toLowerCase() : '';
                            return sortOrder === 'desc' ? bValue.localeCompare(aValue) : aValue.localeCompare(bValue);
                        });
                        break;
                    default:
                        break;
                }
                setSortedRows(sorted.map(item => item.row));
                setSortedOnselectCell(sorted.map(item => item.onSelectCell));
                setSortedIndices(sorted.map(item => item.index));
            }
        } else {
            setSortedRows(rows);
            setSortedOnselectCell(onSelectCell);
            setSortedIndices(null);
        }
    }, [sortStandard, sortOrder, combinedData, header, columnTypes, rows, onSelectCell]);

    const handleSort = (name: string) => {
        if (name === sortStandard) {
            if (sortOrder===null) {
                setSortOrder('asc');
            } else if (sortOrder==='asc') {
                setSortOrder('desc');
            } else{
                setSortOrder(null);
            }
        } else {
            setSortStandard(name);
            setSortOrder('asc');
        }
    };
    
    const processData = (row:any, r:number)=>{
        const rowOffset = rowsPerPage * pageIndex + r;
        // Map this displayed position back to the row's position in the props, so
        // selection stays attached to the right row once sorted.
        const sourceIndex = sortedIndices ? sortedIndices[rowOffset] : rowOffset;
        const currentOnSelectCell = sortedOnSelectCell && sortedOnSelectCell[rowOffset];
        const currentOnSelectRow = onSelectRow && onSelectRow[sourceIndex];
        const currentRowSelected = rowsSelected && rowsSelected[sourceIndex];
        // Read through the same permutation as the rest, so opting a row out
        // of selection follows it when the table is sorted.
        const currentRowSelectable = !rowsSelectable || rowsSelectable[sourceIndex] !== false;
        const toggleRow = (event: React.MouseEvent) => {
            // Keep the checkbox from also triggering the row handler in 'row' mode.
            event.stopPropagation();
            currentOnSelectRow && currentOnSelectRow();
        };
        return (
            <tr style={{cursor: (selectType === 'row' && onSelectRow) ? 'pointer': 'default'}}
                className={selectType === 'row' && currentRowSelected
                    ? 'table__rowSelected'
                    : ''
                }
                onClick={() => selectType === 'row' && currentOnSelectRow && currentOnSelectRow()}
            >
                {selectType === 'checkBox' &&
                    <td>
                        {!currentRowSelectable
                            ?   <></>
                            :   currentRowSelected
                            ?   <MdOutlineCheckBox
                                    className='table__checkBox'
                                    onClick={toggleRow}
                                />
                            :   <MdOutlineCheckBoxOutlineBlank
                                    className='table__checkBox--blank'
                                    onClick={toggleRow}
                                />
                        }
                    </td>

                }
                {row.map((data:any, i:number) => ((currentOnSelectCell && currentOnSelectCell[i])
                    ?   <td
                            // className={`table__tableLink ${className}`}
                        >
                            <div className='table__tableLink_content' onClick={currentOnSelectCell[i]}>
                                {data !== null ? data : '-'}
                            </div>
                        </td>
                    // :   <td>{data ? (data instanceof Date ? data.toLocaleDateString('en-us', { year: 'numeric', month: 'numeric', day: 'numeric' }) : data) : '-'}</td>
                    :   <td>{data !== null ? (data instanceof Date ? data.toLocaleString('en-us', { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' }) : data) : '-'}</td>
                ))}
            </tr>
        )
    };

    useEffect(()=>{
        if (!navigator) {
            setRowsPerPage(rows.length);
        }
    }, [rows]);

    return (
        <div className={`table ${className}`}>
            <div className='table__tableWrapper'>
                <table className='table__table' ref={tableRef}>
                    <tbody>
                        <tr>
                            {selectType === 'checkBox' &&
                                <th className='table_headerDefault'>
                                    {onSelectAll &&
                                        (pageAllSelected
                                            ?   <MdOutlineCheckBox
                                                    className='table__checkBox'
                                                    onClick={() => onSelectAll(pageSourceIndices)}
                                                />
                                            :   <MdOutlineCheckBoxOutlineBlank
                                                    className='table__checkBox--blank'
                                                    onClick={() => onSelectAll(pageSourceIndices)}
                                                />
                                        )
                                    }
                                </th>
                            }
                            {header.map((name,n) => (                         
                                // <th className={columnTypes?'table_header':null} >{name} </th>
                                <th className={columnTypes?((sortOrder==='asc'||sortOrder==='desc')&&sortStandard==name?'table_headerStandard':(columnTypes[n]?'table_header':'table_headerInactive')):'table_headerDefault'} onClick={()=>columnTypes && columnTypes[n] && handleSort(name)}> 
                                    {(columnTypes && columnTypes[n])
                                        ?   <div className='table_headerColumn'>
                                                <div className='table_headerColumnName'>{name}</div> 
                                                {columnTypes && columnTypes[n] && 
                                                    <div className='table_columnIcon'>
                                                        <VscTriangleUp className={(sortOrder==='asc'&&sortStandard==name)&& 'table_columnIconClicked'} />
                                                        <VscTriangleDown className={(sortOrder==='desc'&&sortStandard==name)&& 'table_columnIconClicked'} />
                                                    </div>
                                                }
                                            </div>
                                        :  <div className='table_headerColumnName'>{name}</div>
                                    }
                                </th>
                            ))}
                        </tr>
                        {sortOrder!==null?(sortedRows.slice(rowsPerPage*pageIndex, rowsPerPage*(pageIndex + 1)).map((row, r) => 
                            processData(row, r))):
                            (rows.slice(rowsPerPage*pageIndex, rowsPerPage*(pageIndex + 1)).map((row, r) => processData(row, r)))
                        }
                    </tbody>
                </table>
            </div>
            {navigator &&
                <div className='table__navigator'>
                    <p className='table__navigatorText'>Rows per page:</p>
                    <div
                        className='table__navigatorDropdown'
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                        ref={rowsPerPageRef}
                    >
                        <p>{rowsPerPage}</p>
                        <BiCaretDown className='table__navigatorDropdownIcon' />
                        {dropdownOpen &&
                            <div className='table__navigatorDropdownMenu'>
                                {ROWS_PER_PAGE.map(numRows => (
                                    <p className='table__navigatorDropdownItem' onClick={() => setRowsPerPage(numRows)}>
                                        {numRows}
                                    </p>
                                ))}
                            </div>
                        }
                    </div>
                    <p className='table__navigatorText'>
                        {rowsPerPage*pageIndex + 1}-{Math.min(rows.length, rowsPerPage*pageIndex + rowsPerPage)} of {rows.length}
                    </p>
                    <MdChevronLeft className={pageIndex > 0
                        ? 'table__navigatorArrow'
                        : 'table__navigatorArrow--disabled'
                    } onClick={() => pageIndex > 0 && setPageIndex(pageIndex - 1)} />
                    <MdChevronRight className={rows.length > rowsPerPage*(pageIndex + 1)
                        ? 'table__navigatorArrow'
                        : 'table__navigatorArrow--disabled'
                    } onClick={() => rows.length > rowsPerPage*(pageIndex + 1) && setPageIndex(pageIndex + 1)} />
                </div>
            }
        </div>
    );
}

export default Table;